/**
 * Home's For Review state: the triage items built from the practice payload,
 * minus what the attorney dismissed; the active filter; which row is open
 * (the briefing by default); prev/next stepping; and dismiss/complete with
 * an Undo toast that puts the row back.
 */
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { useAgentAttention } from "@/features/agentAttention/context/AgentAttentionProvider";

import type { PracticeDashboard } from "../types";
import { groupByKind } from "./homeKinds";
import {
  applyTriageFilter,
  buildTriage,
  groupTriage,
  useTriageDismissed,
  useTriageReadState,
  waitsOnAttorney,
  type TriageFilter,
  type TriageGroup,
  type TriageItem
} from "./triageModel";

export const BRIEFING_ID = "briefing";
export const UNDO_TOAST_MS = 4000;

export type HomeTriage = {
  /** Live items (not dismissed), in rail order and unfiltered. */
  items: TriageItem[];
  /** Live items after the filter. */
  visible: TriageItem[];
  groups: TriageGroup[];
  filter: TriageFilter;
  setFilter: (f: TriageFilter) => void;
  isUnread: (item: TriageItem) => boolean;
  unreadCount: number;
  briefingRead: boolean;
  markAllRead: () => void;
  /** `"briefing"` or an item id. */
  selectedId: string;
  /** The open item, or null when the briefing is open. */
  selected: TriageItem | null;
  select: (id: string) => void;
  canPrev: boolean;
  canNext: boolean;
  step: (dir: 1 | -1) => void;
  /**
   * Hide an item (until `until` when snoozing), move to the next row and
   * offer Undo. `verb` leads the toast: "Dismissed: …" / "Done: …".
   */
  remove: (item: TriageItem, verb: string, until?: Date) => void;
};

export const useHomeTriage = (
  summary: PracticeDashboard | null,
  uid: string | null
): HomeTriage => {
  const { items: signals } = useAgentAttention();
  const all = useMemo(
    () => buildTriage(summary, uid ?? "", signals),
    [summary, uid, signals]
  );
  const read = useTriageReadState(uid);
  const dismissed = useTriageDismissed(uid);
  const [filter, setFilter] = useState<TriageFilter>("all");
  const [selectedId, setSelectedId] = useState<string>(BRIEFING_ID);

  const items = useMemo(
    () => all.filter((t) => !dismissed.isDismissed(t)),
    [all, dismissed]
  );
  const isUnread = useCallback(
    (item: TriageItem) => waitsOnAttorney(item) && !read.isRead(item),
    [read]
  );
  const visible = useMemo(
    () => applyTriageFilter(items, filter, isUnread),
    [items, filter, isUnread]
  );
  const groups = useMemo(() => groupTriage(visible), [visible]);
  const unreadCount = useMemo(
    () => items.filter(isUnread).length + (read.briefingRead ? 0 : 1),
    [items, isUnread, read.briefingRead]
  );

  const selected = useMemo(
    () => items.find((t) => t.id === selectedId) ?? null,
    [items, selectedId]
  );
  const effectiveId = selected ? selectedId : BRIEFING_ID;

  const order = useMemo(
    () => [
      BRIEFING_ID,
      ...groupByKind(visible).flatMap((g) => g.rows.map((t) => t.id))
    ],
    [visible]
  );
  const index = Math.max(0, order.indexOf(effectiveId));

  const select = useCallback(
    (id: string) => {
      setSelectedId(id);
      if (id === BRIEFING_ID) {
        read.markBriefingRead();
        return;
      }
      const item = items.find((t) => t.id === id);
      if (item) read.markRead(item);
    },
    [items, read]
  );

  const step = useCallback(
    (dir: 1 | -1) => {
      const next = order[Math.min(order.length - 1, Math.max(0, index + dir))];
      if (next && next !== effectiveId) select(next);
    },
    [effectiveId, index, order, select]
  );

  const markAllRead = useCallback(() => {
    read.markRead(items);
    read.markBriefingRead();
  }, [items, read]);

  const remove = useCallback(
    (item: TriageItem, verb: string, until?: Date) => {
      const at = order.indexOf(item.id);
      const nextId =
        at >= 0 ? order[at + 1] ?? order[at - 1] ?? BRIEFING_ID : effectiveId;
      dismissed.dismiss(item, until);
      if (effectiveId === item.id)
        select(nextId === item.id ? BRIEFING_ID : nextId);
      toast(`${verb}: ${item.title}`, {
        duration: UNDO_TOAST_MS,
        action: {
          label: "Undo",
          onClick: () => {
            dismissed.restore(item);
            setSelectedId(item.id);
          }
        }
      });
    },
    [dismissed, effectiveId, order, select]
  );

  return {
    items,
    visible,
    groups,
    filter,
    setFilter,
    isUnread,
    unreadCount,
    briefingRead: read.briefingRead,
    markAllRead,
    selectedId: effectiveId,
    selected,
    select,
    canPrev: index > 0,
    canNext: index < order.length - 1,
    step,
    remove
  };
};
